// "Included crews" — which crews a partner may sell (migration 0241).
//
// The supply-side twin of PartnerLocationsFieldset, and it saves itself for
// exactly the same reason: inclusion is not a property of the form around it.
// Folding it into the main editor Save would mean that changing a commission
// rate re-sends the crew list, and a list stale by a minute (someone else
// onboarded a crew meanwhile) would quietly take a crew away.
//
// Two shapes, one control: "All crews" hides the list entirely, because a list
// under a ticked All box reads as a restriction it is not. Saving it also
// CLEARS the stored crews — the two are never both populated — so the hint
// says so before the click. DriveCars' own partners are locked into it: every
// customer on drivecars.ai shops through one of those rows, and the API
// refuses `all: false` for them with a 409 whatever this UI does.
//
// WHAT IS NOT IN THE CITIES VERSION: the warning badge. A partner on a
// supplier-commission model earns from what the crew collects, so a crew that
// has agreed no rate is NOT served to it — even though ops ticked it. That is
// a silent disagreement between the saved list and the live fleet, and the
// badge is the only place it becomes visible. The rate itself is edited on the
// crew's own page, not here: it is the crew's fact, not the partner's.
//
// The arithmetic (filtering, counts, which warnings belong on screen) is in
// partner-vendors-state.ts so it can be tested without a DOM.

import { useEffect, useMemo, useState } from 'react';
import {
  fetchAdminVendors,
  fetchPartnerVendors,
  fetchPartnerVendorWarnings,
  savePartnerVendors,
  type AdminVendorRow,
} from '@/lib/admin-api';
import { ApiError } from '@/lib/api';
import {
  filterCrews,
  inclusionCount,
  toggleCrew,
  warningsBySelectedCrew,
  type CrewWarning,
  type IncludableCrew,
} from './partner-vendors-state';

export interface PartnerVendorsFieldsetProps {
  partnerId: number;
  /** DriveCars' own storefront/app rows. All crews is locked ON for them —
   *  shown disabled rather than hidden, so ops can see WHY it cannot be
   *  changed instead of wondering where the control went. */
  isDriveCarsPartner: boolean;
  /** Which commercial model the partner is on. The two supplier-commission
   *  models are the ones where an unrated crew is silently not served, and
   *  therefore the only ones that render warning badges. */
  commercialModel: string;
}

/** One page of the crews list. The endpoint caps a page at 100, so the loop
 *  below pages until it has them all rather than truncating the fleet at an
 *  arbitrary line and leaving ops unable to tick a crew that exists. */
const CREW_PAGE_LIMIT = 100;
/** A stop so a paging bug cannot spin forever against a live API. 50 pages is
 *  5,000 crews — an order of magnitude past any real fleet. */
const MAX_CREW_PAGES = 50;

const SUPPLIER_COMMISSION_MODELS = new Set([
  'supplier_commission',
  'markup_supplier_commission',
]);

function toCrew(row: AdminVendorRow): IncludableCrew {
  return {
    id: row.id,
    // displayName is what the crews list already renders; the email is the
    // fallback so an unnamed row is still identifiable enough to tick.
    name: row.displayName?.trim() || row.businessName?.trim() || row.email,
  };
}

/** Every ACTIVE crew, paged until exhausted. The house crew and the Avis
 *  supplier are dropped: the API refuses them (400 `vendor_not_allowed`), so
 *  offering them here would only produce a ticked box that cannot be saved. */
async function loadAllActiveCrews(signal?: AbortSignal): Promise<IncludableCrew[]> {
  const out: IncludableCrew[] = [];
  for (let page = 1; page <= MAX_CREW_PAGES; page += 1) {
    const res = await fetchAdminVendors({ status: 'active', limit: CREW_PAGE_LIMIT, page }, signal);
    const rows = res.rows ?? [];
    for (const row of rows) {
      if (row.isHouse || row.source === 'avis') continue;
      out.push(toCrew(row));
    }
    // Short page, or we already hold everything the server says exists.
    if (rows.length < CREW_PAGE_LIMIT) break;
    if (typeof res.total === 'number' && page * CREW_PAGE_LIMIT >= res.total) break;
  }
  return out;
}

export function PartnerVendorsFieldset({
  partnerId,
  isDriveCarsPartner,
  commercialModel,
}: PartnerVendorsFieldsetProps) {
  const [all, setAll] = useState(isDriveCarsPartner);
  const [selected, setSelected] = useState<number[]>([]);
  const [crews, setCrews] = useState<IncludableCrew[]>([]);
  const [warnings, setWarnings] = useState<CrewWarning[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showsWarnings = SUPPLIER_COMMISSION_MODELS.has(commercialModel);

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise.all([
      fetchPartnerVendors(partnerId),
      // Asked for even when `all` is on: unticking All crews must not then
      // wait on a round-trip before showing anything to tick.
      loadAllActiveCrews(),
      // Only on the models where an unrated crew actually changes what is
      // served — a badge about a field that does not apply is noise.
      showsWarnings
        ? fetchPartnerVendorWarnings(partnerId).then((r) => r.warnings)
        : Promise.resolve([] as CrewWarning[]),
    ])
      .then(([scope, list, warns]) => {
        if (!live) return;
        setAll(scope.all);
        setSelected(scope.vendorIds);
        setCrews(list);
        setWarnings(warns);
        setError(null);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : 'Could not load crews.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [partnerId, showsWarnings]);

  const visible = useMemo(
    () => filterCrews(crews, selected, query),
    [crews, selected, query],
  );
  const warningFor = useMemo(
    () => warningsBySelectedCrew(warnings, selected),
    [warnings, selected],
  );
  const count = inclusionCount(selected);

  const summary = all
    ? 'All crews'
    : `${count} ${count === 1 ? 'crew' : 'crews'} included`;

  const handleSave = () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    // `all` is always sent, `vendorIds` only when it is the thing being set —
    // a list alongside `all: true` would contradict the server, which clears
    // the rows when the flag goes on.
    savePartnerVendors(partnerId, all ? { all: true } : { all: false, vendorIds: selected })
      .then((res) => {
        setAll(res.all);
        setSelected(res.vendorIds);
        setSaved(true);
        // The set that is served may have changed with the set that is saved.
        if (showsWarnings) {
          fetchPartnerVendorWarnings(partnerId)
            .then((r) => setWarnings(r.warnings))
            .catch(() => {
              /* the badge is advisory; a failed refresh must not fail the save */
            });
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          const body = err.body as { error?: string } | undefined;
          if (body?.error === 'first_party_everywhere') {
            setError('DriveCars’ own partners sell every crew and cannot be narrowed.');
            return;
          }
          if (body?.error === 'vendor_not_allowed') {
            setError('Only local crews can be included — not the house crew or a supplier.');
            return;
          }
        }
        setError(err instanceof Error ? err.message : 'Could not save included crews.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <fieldset>
      <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
        Included crews
      </legend>
      <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
        Which crews this partner may sell. Crews left out are never shown to them.
        Saved on its own — the Save below does not touch the rest of this form.
      </p>

      <label className="mt-1.5 flex items-center gap-1.5 text-[13px]">
        <input
          type="checkbox"
          aria-label="All crews"
          checked={all}
          disabled={isDriveCarsPartner || loading}
          onChange={() => {
            setAll((v) => !v);
            setSaved(false);
          }}
        />
        <span>All crews</span>
      </label>
      {isDriveCarsPartner ? (
        <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
          This is DriveCars’ own partner row — it sells every crew and cannot be narrowed.
        </p>
      ) : (
        <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
          Turning this on clears the crew list.
        </p>
      )}

      {loading ? (
        <p className="mt-2 text-[12px] text-[color:var(--bo-mute)]">Loading crews…</p>
      ) : null}

      {!all && !loading ? (
        <>
          <input
            type="search"
            aria-label="Search crews"
            placeholder="Search crews…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="mt-2 h-9 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13px]"
          />
          <div className="mt-2 max-h-72 space-y-1 overflow-y-auto rounded-xl border border-[color:var(--bo-line)] p-3">
            {crews.length === 0 ? (
              <p className="text-[12px] text-[color:var(--bo-mute)]">No active crews to include.</p>
            ) : visible.length === 0 ? (
              <p className="text-[12px] text-[color:var(--bo-mute)]">No crews match that search.</p>
            ) : (
              visible.map((crew) => (
                <div key={crew.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <label className="flex items-center gap-1.5 text-[13px]">
                    <input
                      type="checkbox"
                      aria-label={crew.name}
                      checked={selected.includes(crew.id)}
                      onChange={() => {
                        setSelected((cur) => toggleCrew(cur, crew.id));
                        setSaved(false);
                      }}
                    />
                    <span>{crew.name}</span>
                  </label>
                  {warningFor.has(crew.id) ? (
                    <span className="rounded-full bg-[#fef3c7] px-2 py-0.5 text-[11px] text-[#92400e]">
                      No supplier commission rate — hidden from this partner
                    </span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </>
      ) : null}

      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          disabled={saving || loading}
          onClick={handleSave}
          className="inline-flex h-9 shrink-0 items-center rounded-lg bg-[color:var(--bo-ink)] px-3 text-[12px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save included crews'}
        </button>
        <span className="text-[11.5px] text-[color:var(--bo-mute)]">{summary}</span>
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-[11.5px] text-[#9f1239]">
          {error}
        </p>
      ) : null}
      {saved && !error ? (
        <p role="status" className="mt-2 text-[11.5px] text-[#166534]">
          Included crews saved.
        </p>
      ) : null}
    </fieldset>
  );
}
